<template>
    <IntegrationModal
        title="Campaign Monitor"
        documentation-url="https://support.topol.io/en/articles/8283109-export-to-campaign-monitor"
        :invalid-validation="v$.$invalid"
        :loading="loading"
        :edit-mode="integration !== undefined"
        @close="$emit('close')"
        @create="createOAuthUrl"
        @edit="updateIntegration"
    >
        <template #inputs>
            <div class="mb-6 w-full">
                <TextInput
                    v-model="v$.name.$model"
                    class="mt-6"
                    placeholder="Enter your preferred integration name"
                    label="Name"
                    :error="getError('name')"
                />
            </div>
        </template>
    </IntegrationModal>
</template>

<script lang="ts" setup>
import { route } from 'ziggy-js';
import { create } from '@composables/useNotifications';
import useVuelidate from '@vuelidate/core';
import { maxLength } from '@vuelidate/validators';
import IInputErrors from '@/types/Atomic/IInputErrors';
import { PropType } from 'vue';
import { IBrevo, IIntegration } from '@/types/Integrations/IIntegration';
import { AxiosError } from 'axios';

const props = defineProps({
    integration: {
        type: Object as PropType<IIntegration<IBrevo>>,
        required: false,
        default: undefined,
    },
});
const emit = defineEmits(['close']);

const loading = ref(false);
const errors = ref<IInputErrors>({});

const state = reactive({
    name: props.integration?.name || '',
});

const rules = {
    name: {
        maxLength: maxLength(255),
    },
};

const v$ = useVuelidate(rules, state);

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || errors.value?.[field] || '';
};

const createOAuthUrl = async () => {
    v$.value.$touch();
    if (v$.value.$error) {
        return;
    } else {
        loading.value = true;
        try {
            const result = await axios.get(
                route('integrations.campaign-monitor.o-auth-two-url', {
                    name: state.name,
                })
            );

            if (result.data.success) {
                emit('close');
                window.location.href = result.data.data;
            } else {
                create({
                    title: 'Integration not added',
                    text: 'There was an error adding Campaign Monitor integration.',
                    type: 'error',
                });
            }
        } catch (e) {
            create({
                title: 'Integration not added',
                text:
                    e instanceof AxiosError
                        ? e.response?.data?.message ??
                          'There was an error adding Campaign Monitor integration.'
                        : 'There was an error adding Campaign Monitor integration.',
                type: 'error',
            });
        } finally {
            loading.value = false;
        }
    }
};

const updateIntegration = () => {
    v$.value.$touch();
    if (v$.value.$error || !props.integration) {
        return;
    } else {
        loading.value = true;
        router.put(
            route('integrations.campaign-monitor.update', {
                integration: props.integration.id,
            }),
            {
                name: state.name,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    emit('close');
                    create({
                        title: 'Integration updated',
                        type: 'success',
                    });
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: 'Integration not updated',
                        text:
                            err.message ??
                            'There was an error updating Campaign Monitor integration.',
                        type: 'error',
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            }
        );
    }
};
</script>

<style scoped></style>
